import styled from '@emotion/styled';
import {
  Button,
  InfoColumnIcon,
  Tooltip,
  Typography,
} from '@innovamat/glimmer-components';
import {
  AdminSublabel,
  AdminSublabelInfoIcon,
} from '../admin-dashboard.styled';
import AdquiredContentBar from '../components/adquired-content-bar';
import AverageInnovamat from '../components/average-innovamat/average-innovamat';
import AverageScoreBar from '../components/average-score-bar';
import AverageTime from '../components/average-time/average-time';
import ClassroomElement from '../components/classroom-element';
import CompletedSessions from '../components/completed-sessions';
import ConnectedPercentage from '../components/connected-percentage/connected-percentage';
import CourseElement from '../components/course-element';
import PlaceholderCell from '../components/placeholder-cell';
import Teachers from '../components/teachers';
import type { TeachersProps } from '../components/teachers/teachers';

export type Sublabel =
  | string
  | {
      text: string;
      tooltip?: string;
      infoTooltip?: string;
      isHidden?: boolean;
      dataTestId?: string;
    };

type MultipleSublabels = {
  values: Sublabel[];
  proportions: string;
};

type MultipleColumn = {
  component: JSX.Element;
  isHidden?: boolean;
};

type MultipleProps = {
  values: MultipleColumn[];
  proportions: string;
};

type CompletedSessionsProps = {
  completed: number;
  total: number;
  lab?: number;
  adv?: number;
  rowIsNull: boolean;
  classroomName: string;
  courseOrder: number;
};

type DigitalPracticeProps = {
  value: {
    avgScore: number;
    studentsPerStrip: number[];
    avgInnovamatScore: number;
    acquiredContent: number;
  };
  proportions: string;
  rowIsNull: boolean;
  classroomName: string;
  courseOrder: number;
};

type StudentsProps = {
  value: {
    connectedPercentage: number;
    timeSpent: number;
  };
  proportions: string;
  classroomName: string;
  rowIsNull: boolean;
  courseOrder: number;
};

type ClassroomElementProps = {
  value: string;
  classroomId: string;
  classroomAlias: string;
  courseOrder: number;
  goToClassroom?: (classroomId: string) => void;
};

type TestsProps = {
  value: {
    numberOfAvailableTestReports: number;
    hasPermission: boolean;
    rounds: {
      roundName: string;
      participation: {
        total: number;
        completed: number;
      };
    }[];
  };
  classroomId: string;
  goToTestsAndResults?: (classroomId: string) => void;
  t: (key: string) => string;
};

const MultipleContainer = styled.div<{ proportions: string }>`
  display: grid;
  gap: 8px;
  grid-template-columns: ${({ proportions }) => proportions};
  width: 100%;
`;

const Sublabel = styled(Typography.Body3)`
  display: flex;
  align-items: flex-end;
`;

const renderCourseElement = (value: {
  courseName: string;
  courseOrder: number;
}): JSX.Element => {
  const { courseName, courseOrder } = value;
  return (
    <CourseElement
      text={courseName}
      courseName={courseName}
      courseOrder={courseOrder}
    />
  );
};

const renderClassroom = ({
  value,
  classroomId,
  classroomAlias,
  courseOrder,
  goToClassroom,
}: ClassroomElementProps): JSX.Element => {
  return (
    <ClassroomElement
      text={value}
      classroomAlias={classroomAlias}
      classroomId={classroomId}
      courseOrder={courseOrder}
      goToClassroom={goToClassroom}
    />
  );
};

const renderSubLabel = (sublabel: Sublabel): JSX.Element => {
  if (typeof sublabel === 'string') {
    return <AdminSublabel>{sublabel}</AdminSublabel>;
  }

  const { text, tooltip, infoTooltip, dataTestId } = sublabel;
  return (
    <AdminSublabel data-testid={dataTestId}>
      <Tooltip content={tooltip} popperOptions={{ strategy: 'fixed' }}>
        <div>{text}</div>
      </Tooltip>
      {infoTooltip && (
        <AdminSublabelInfoIcon>
          <InfoColumnIcon tooltipText={infoTooltip} />
        </AdminSublabelInfoIcon>
      )}
    </AdminSublabel>
  );
};

const getVisibleProportions = (
  proportions: string,
  visibleValues: Sublabel[] | MultipleColumn[]
): string => {
  return proportions.split(' ').slice(0, visibleValues.length).join(' ');
};

const renderMultipleSublabels = ({
  values,
  proportions,
}: MultipleSublabels): JSX.Element => {
  const visibleValues = values.filter(
    (value) => typeof value === 'string' || !value.isHidden
  );
  return (
    <MultipleContainer
      proportions={getVisibleProportions(proportions, visibleValues)}
    >
      {visibleValues.map((value) => renderSubLabel(value))}
    </MultipleContainer>
  );
};

const renderMultipleColumns = ({
  values,
  proportions,
}: MultipleProps): JSX.Element => {
  const visibleValues = values.filter((value) => !value.isHidden);
  return (
    <MultipleContainer
      proportions={getVisibleProportions(proportions, visibleValues)}
    >
      {visibleValues.map((value) => value.component)}
    </MultipleContainer>
  );
};

const renderDigitalPractice = ({
  value,
  proportions,
  rowIsNull,
  classroomName,
  courseOrder,
}: DigitalPracticeProps): JSX.Element => {
  const { avgInnovamatScore, studentsPerStrip, avgScore, acquiredContent } =
    value;
  return renderMultipleColumns({
    values: [
      {
        component: (
          <AverageScoreBar
            score={avgScore}
            studentsPerStrip={studentsPerStrip}
            rowIsNull={rowIsNull}
            classroomName={classroomName}
            courseOrder={courseOrder}
          />
        ),
      },
      {
        component: <AverageInnovamat avgInnovamatScore={avgInnovamatScore} />,
        isHidden: true,
      },
      {
        component: (
          <AdquiredContentBar value={acquiredContent} rowIsNull={rowIsNull} />
        ),
        isHidden: true,
      },
    ],
    proportions: proportions,
  });
};

const renderTests = ({
  value,
  goToTestsAndResults,
  classroomId,
  t,
}: TestsProps): JSX.Element => {
  let tooltipContent;

  if (!value.hasPermission) {
    tooltipContent = t('reports.admin.tests.tooltip.noPermission');
  } else if (value.numberOfAvailableTestReports) {
    tooltipContent = value.rounds?.map((round) => (
      <p>
        {`${t(round.roundName)}: ${round.participation.completed}/${
          round.participation.total
        }`}
      </p>
    ));
  }

  return (
    <Tooltip content={tooltipContent} popperOptions={{ strategy: 'fixed' }}>
      <Button
        variant="secondary"
        size="S"
        rightIcon="NextWithTextIcon"
        oneLine
        onClick={() => goToTestsAndResults?.(classroomId)}
        disabled={
          !value.hasPermission || value.numberOfAvailableTestReports === 0
        }
      >
        {value.numberOfAvailableTestReports}
      </Button>
    </Tooltip>
  );
};

const renderStudents = ({
  value,
  proportions,
  classroomName,
  courseOrder,
  rowIsNull,
}: StudentsProps): JSX.Element => {
  const { connectedPercentage, timeSpent } = value;
  return renderMultipleColumns({
    values: [
      {
        component: (
          <ConnectedPercentage
            connectedPercentage={connectedPercentage}
            classroomName={classroomName}
            courseOrder={courseOrder}
            rowIsNull={rowIsNull}
          />
        ),
      },
      {
        component: (
          <AverageTime
            timeSpent={timeSpent}
            rowIsNull={rowIsNull}
            classroomName={classroomName}
            courseOrder={courseOrder}
          />
        ),
      },
    ],
    proportions: proportions,
  });
};

const renderCompletedSessions = ({
  completed,
  total,
  rowIsNull,
  lab,
  adv,
  classroomName,
  courseOrder,
}: CompletedSessionsProps): JSX.Element => {
  return (
    <CompletedSessions
      completed={completed}
      total={total}
      lab={lab}
      adv={adv}
      rowIsNull={rowIsNull}
      classroomName={classroomName}
      courseOrder={courseOrder}
    />
  );
};

const renderTeachers = ({
  firstName,
  lastName,
  lastConnection,
  rowIsNull,
  classroomName,
  courseOrder,
}: TeachersProps & { rowIsNull: boolean }): JSX.Element => {
  if (rowIsNull || !firstName || !lastConnection)
    return <PlaceholderCell left />;
  return (
    <Teachers
      firstName={firstName}
      lastName={lastName}
      lastConnection={lastConnection}
      classroomName={classroomName}
      courseOrder={courseOrder}
    />
  );
};

export {
  renderClassroom,
  renderCompletedSessions,
  renderCourseElement,
  renderDigitalPractice,
  renderMultipleColumns,
  renderMultipleSublabels,
  renderStudents,
  renderTeachers,
  renderTests,
};
